import React, { useEffect, useRef, useCallback, useState } from "react";
import { useMemo } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { useDispatch } from "react-redux";
import { usePrevious, useUpdateEffect } from "react-use";
import { AddCrossTinyIcon, IconButton, ScrollShadow } from "@activecollab/components";
import classNames from "classnames";
import { List } from "immutable";
import PropTypes from "prop-types";
import { storageOverusedError } from "~/store/actions/actions.httpMessages.jsx";
import { addUploadedFile } from "~/store/actions/actions.uploadFile.jsx";
import { StorageOverusedError } from "~/utils/errors/StorageOverusedError.js";
import { AttachmentAsListItemContainer } from "./AttachmentAsListItemContainer.jsx";
import { AttachmentMenuPicker } from "./menu/index.jsx";
import { SelectAttachmentsItem } from "./SelectAttachmentsItem.js";
import { UploadedFileAsListItemContainer } from "./UploadedFileAsListItemContainer.js";
import { useFileIntegrations } from "../Hooks/useFileIntegrations.jsx";

export const AttachmentsList = ({
  attachmentIds,
  filesToAttach: defaultFilesToAttach,
  filesToDrop: defaultFilesToDrop,
  onChange,
  className,
  openFileWindow,
  uploads,
  uploading,
  uploaded,
  onCancelUpload,
  onRetryUpload,
  onAttachFromDropbox,
  onAttachFromGoogleDrive,
  error,
  dropUploadedFile,
}) => {
  const dispatch = useDispatch();
  const { hasAttachmentIntegration } = useFileIntegrations();

  const [filesToAttach, setFilesToAttach] = useState(defaultFilesToAttach);
  const [filesToDrop, setFilesToDrop] = useState(defaultFilesToDrop);

  const attachmentWrapper = useRef();
  const previousFilesToAttach = usePrevious(filesToAttach);

  const uploadsList = useMemo(() => {
    return Object.values(uploads);
  }, [uploads]);

  const handleRemoveAttachment = useCallback((id) => {
    setFilesToDrop((prevState) => [...new Set([...prevState, id])]);
  }, []);

  const handleRemoveUploadedFile = useCallback(
    (code) => {
      setFilesToAttach((prevState) => prevState.filter((value) => value !== code));
      dropUploadedFile({ code });
    },
    [dropUploadedFile]
  );

  useUpdateEffect(() => {
    setFilesToAttach(defaultFilesToAttach);
  }, [defaultFilesToAttach]);

  useUpdateEffect(() => {
    setFilesToDrop(defaultFilesToDrop);
  }, [defaultFilesToDrop]);

  useEffect(() => {
    if (error instanceof StorageOverusedError) {
      dispatch(storageOverusedError(error));
    }
  }, [dispatch, error]);

  const previousUploaded = usePrevious(uploaded);

  useEffect(() => {
    const previousUploadedCodes = previousUploaded ? previousUploaded.map(({ code }) => code) : [];

    const difference = uploaded.filter(({ code }) => !previousUploadedCodes.includes(code));

    if (difference.length) {
      const codes = [];
      difference.forEach((file) => {
        codes.push(file.code);
        dispatch(addUploadedFile(file));
      });
      setFilesToAttach((prevState) => [...new Set([...prevState, ...codes])]);
    }
  }, [uploaded, previousUploaded, dispatch]);

  useUpdateEffect(() => {
    typeof onChange === "function" && onChange({ filesToAttach, filesToDrop });
  }, [onChange, filesToAttach, filesToDrop]);

  useEffect(() => {
    const scrollHeight = attachmentWrapper?.current?.view?.scrollHeight;
    const offsetHeight = attachmentWrapper?.current?.view?.offsetHeight;
    if (
      attachmentWrapper.current &&
      scrollHeight > offsetHeight &&
      uploadsList.length === 0 &&
      (attachmentIds.size > 0 || filesToAttach.length > previousFilesToAttach?.length) &&
      !uploading
    ) {
      attachmentWrapper?.current?.view?.scroll({
        top: scrollHeight,
        left: 0,
        behavior: "smooth",
      });
    }
  }, [uploading, uploadsList, attachmentIds, filesToAttach, previousFilesToAttach?.length]);

  const filteredAttachmentIds = useMemo(() => {
    return attachmentIds.filter((id) => !filesToDrop.includes(id));
  }, [attachmentIds, filesToDrop]);

  return (
    <div className={classNames("attachments-wrapper tw-flex tw-flex-1", className)}>
      <ScrollShadow className="tw-flex-1 tw-min-w-0">
        {({ onScroll }) => (
          <Scrollbars
            autoHeight
            className="tw-h-full"
            autoHeightMin={0}
            autoHeightMax={136}
            onScroll={onScroll}
            ref={attachmentWrapper}
          >
            <div className="attachments-grid-wrapper">
              {filteredAttachmentIds.map((id) => (
                <AttachmentAsListItemContainer
                  key={id}
                  id={id}
                  onRemove={handleRemoveAttachment}
                />
              ))}
              {filesToAttach.map((code) => (
                <UploadedFileAsListItemContainer
                  key={code}
                  id={code}
                  onRemove={handleRemoveUploadedFile}
                />
              ))}
              {uploadsList.map((u) => (
                <SelectAttachmentsItem
                  key={u.code}
                  id={u.code}
                  name={u.name}
                  type="local"
                  uploading={u.uploading}
                  progress={u.progress}
                  errorMessage={u.errorMessage}
                  error={u.error}
                  onRemove={onCancelUpload}
                  onRetry={onRetryUpload}
                />
              ))}
              {hasAttachmentIntegration ? (
                <AttachmentMenuPicker
                  openFileWindow={openFileWindow}
                  onAttachFromDropbox={onAttachFromDropbox}
                  onAttachFromGoogleDrive={onAttachFromGoogleDrive}
                />
              ) : (
                <IconButton
                  variant="text colored"
                  className="attachment-btn-add"
                  type="button"
                  onClick={openFileWindow}
                >
                  <AddCrossTinyIcon />
                </IconButton>
              )}
            </div>
          </Scrollbars>
        )}
      </ScrollShadow>
    </div>
  );
};

AttachmentsList.propTypes = {
  attachmentIds: PropTypes.instanceOf(List),
  filesToAttach: PropTypes.arrayOf(PropTypes.string),
  filesToDrop: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func,
  className: PropTypes.string,
  openFileWindow: PropTypes.func,
  uploads: PropTypes.object,
  uploading: PropTypes.bool,
  uploaded: PropTypes.array,
  onCancelUpload: PropTypes.func,
  onRetryUpload: PropTypes.func,
  onAttachFromDropbox: PropTypes.func,
  onAttachFromGoogleDrive: PropTypes.func,
  error: PropTypes.object,
  dropUploadedFile: PropTypes.func,
};

AttachmentsList.defaultProps = {
  attachmentIds: List(),
  filesToAttach: [],
  filesToDrop: [],
  uploads: {},
  uploaded: [],
  uploading: false,
};
